<template>
  <block-item-card
    ref="yield"
    :tooltip="$t('ALM.Section.InvestmentsPortfolio.Yield.InfoTip')"
    class="fill-height d-flex flex-column"
  >
    <v-row class="ma-0">
      <v-col
        class="ma-0 pa-0 py-5 px-4"
        cols="12"
      >
        <div class="chart-container">
          <div class="chart-header">
            <div class="d-flex align-center">
              <h3
                class="chart-title"
                style="color: var(--v-synthesis-ui-purple-02-base);"
              >
                {{ $t('ALM.Section.InvestmentsPortfolio.DynamicEvolution.Yield.Title') }}
              </h3>
              <div class="chart-subtitle">
                {{ $t('ALM.Section.InvestmentsPortfolio.DynamicEvolution.Yield.SubTitle') }}
              </div>
            </div>

            <div class="chart-metric">
              <span class="metric-label">{{ $t('ALM.Section.InvestmentsPortfolio.DynamicEvolution.Yield.UpdateInterval') }}</span>
              <span :class="['metric-value', yieldChange >= 0 ? 'positive' : 'negative']">
                {{ yieldChange >= 0 ? '+' : '-' }}{{ Math.abs(yieldChange) }}%
              </span>
            </div>
          </div>
          <v-lazy>
            <e-chart
              :ref="`echart-yield-${chartRefId}`"
              :autoresize="true"
              :option="yieldChartOptions"
              :style="`height: ${chartHeight}px;`"
              class="chart"
            />
          </v-lazy>
        </div>
      </v-col>
    </v-row>
  </block-item-card>
</template>

  <script>
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import baseEChartColors from '@/theme/default/components/charts/echarts/mixins/common/baseEChartColors'
import i18nRouteMeta from '@/mixins/i18n/i18nRouteMeta'
import riskBaseCurrency from '@/theme/default/views/risks/mixins/riskBaseCurrency'
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'
import riskAnalysisId from '@/theme/default/views/risks/mixins/riskAnalysisId'
import { toNumberFormatNotationShort, computeYAxisBounds, toDecimalSignificant } from '@/lib/utils/helper'

import { use } from 'echarts/core'
import { BarChart, LineChart } from 'echarts/charts'
import { UniversalTransition } from 'echarts/features'
import { CanvasRenderer } from 'echarts/renderers'

import { GridComponent, LegendComponent, TitleComponent, TooltipComponent, TransformComponent } from 'echarts/components'

use([
  BarChart,
  LineChart,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  TransformComponent,
  CanvasRenderer,
  UniversalTransition
])

export default {
  name      : 'Yield',
  components: { BlockItemCard },
  directives: {},
  mixins    : [i18nRouteMeta, riskBaseCurrency, baseEChartColors, chartsColorMixin, riskAnalysisId],
  props     : {},
  dataStore : {
    appToolbarExtended  : 'App.Config.AppToolbarExtended',
    Investments         : 'ALM.Investments.Data',
    riskBaseCurrencyData: 'Risks.BaseCurrency.Data'
  },
  enums: {},
  data () {
    return {
      breakpoint            : (this.appToolbarExtended && this.$vuetify.breakpoint.smAndDown ? 12 : 6),
      chartRefId            : Date.now(),
      selectedInvestmentType: 'Total',
      interestOutcomeSwitch : true
    }
  },
  computed: {
    yieldChange () {
      return this.Investments.YieldsEvolution?.yieldsChange || 0
    },

    chartHeight () {
      return 250
    },

    // Format dates for display (Apr24, May24, etc.)
    formattedDates () {
      if (!this.Investments.YieldsEvolution?.dates) return []
      return this.Investments.YieldsEvolution.dates.map(date => {
        const d = new Date(date)
        const month = d.toLocaleDateString('en-US', { month: 'short' })
        const year = d.getFullYear().toString().slice(-2)
        return `${month}${year}`
      })
    },

    yieldChartOptions () {
      if (!this.Investments.YieldsEvolution) return {}

      return {
        animation        : true,
        animationDuration: 1000,
        animationEasing  : 'quinticOut',
        tooltip          : {
          show           : true,
          trigger        : 'axis',
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' },
          formatter      : (params) => {
            if (!params[0].value || params[0].value === '-') {
              return null // disables tooltip + pointer
            }
            const value = toNumberFormatNotationShort(params[0].value)
            return `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].axisValue}</div>
                      <div style="margin: 4px 0;">
                        <span style="display: inline-block; width: 12px; height: 12px; background: ${params[0].color}; margin-right: 8px;"></span>
                        <span>${this.$t('ALM.Section.InvestmentsPortfolio.DynamicEvolution.Yield.Title')}: ${value}%</span>
                      </div>`
          }
        },
        legend: { show: false },
        grid  : {
          left        : 30,
          top         : 20,
          right       : 10,
          bottom      : 10,
          containLabel: true
        },
        xAxis: {
          type     : 'category',
          data     : this.formattedDates,
          axisLabel: {
            margin  : 30,
            show    : true,
            color   : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize: 12
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-06', 0.3),
              type : 'dashed'
            }
          }
        },
        yAxis: {
          type     : 'value',
          min      : computeYAxisBounds(this.Investments.YieldsEvolution.yields).min,
          max      : computeYAxisBounds(this.Investments.YieldsEvolution.yields).max,
          axisLabel: {
            margin       : -15,
            show         : true,
            color        : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize     : 11,
            inside       : true,
            verticalAlign: 'bottom',
            formatter    : (value) => {
              return toDecimalSignificant(value, 2) + '%'
            }
          },
          interval : computeYAxisBounds(this.Investments.YieldsEvolution.yields).interval,
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-02', 1),
              type : 'dashed',
              width: 2
            }
          }
        },
        series: [{
          name     : 'Yield',
          type     : 'line',
          data     : this.Investments.YieldsEvolution.yields,
          itemStyle: {
            color: this.themeColorRGBA('synthesis-ui-green-02')
          },
          lineStyle: {
            width: 3
          },
          symbol    : 'emptyCircle',
          symbolSize: 10,
          smooth    : true,
          areaStyle : {
            color: {
              type      : 'linear',
              x         : 0,
              y         : 0,
              x2        : 0,
              y2        : 1,
              colorStops: [
                {
                  offset: 0,
                  color : this.themeColorRGBA('synthesis-ui-green-02', 0.3)
                },
                {
                  offset: 1,
                  color : this.themeColorRGBA('synthesis-ui-green-02', 0.05)
                }
              ]
            }
          }
        }]
      }
    }

  },

  watch: {
    appToolbarExtended (val) {
      this.breakpoint = val && this.$vuetify.breakpoint.smAndDown ? 12 : 6
    },

    '$vuetify.breakpoint.smAndDown': {
      handler (val) {
        this.breakpoint = val && this.appToolbarExtended ? 12 : 6
      }
    }
  },

  created () {},
  beforeMount () {},
  mounted () {},
  beforeUpdate () {},
  updated () {},
  destroyed () {},

  methods: {
  }
}
  </script>

  <style scoped>

  .gradient-grey-06-bg {
    background-color: var(--v-synthesis-ui-grey-06-base) !important;
  }

  .chart-title {
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
  }

  .chart-subtitle {
    margin-left: 8px;
    color: var(--v-synthesis-ui-grey-01-base);
    font-size: 10px;
    position: relative;
    top: 1px;
  }

  .metric-label {
    font-size: 10px;
    color: var(--v-synthesis-brand-dark-blue-base);
    margin-bottom: 2px;
  }

  .chart-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 0 8px;
  }

  .chart-metric {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }

  .metric-value {
    font-size: 12px;
    font-weight: 500;
  }

  .metric-value.positive {
    color: #4CAF50;
    font-size: 28px;
  }

  .metric-value.negative {
    color: #FF5722;
    font-size: 28px;
  }

  .chart-legend {
    display: flex;
    gap: 12px;
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    color: #666;
  }

  .legend-color {
    width: 8px;
    height: 8px;
    border-radius: 2px;
  }

  </style>
